import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebouncedValue } from '../../shared/useDebouncedValue';
import {
  Button,
  Callout,
  EmptyState,
  IconCalendar,
  IconInbox,
  IconReceipt,
  IconSearch,
  IconSparkle,
  Input,
  Kbd,
  PageHeaderBand,
  Pagination,
  RefreshFailed,
  RefreshStatus,
  Segmented,
  Select,
  Tag,
  Workspace,
} from '../../ui/v2';
import { InquiryDetail } from './InquiryDetail';
import { InquiryCompactList, InquiryTable } from './InquiryList';
import { useInquiryActions, useInquiryList, useInquirySummary } from './data';
import type { InquiryKindConfig } from './kind';
import {
  type EmailFilter,
  type SortKey,
  type View,
  EMAIL_FILTERS,
  EMAIL_FILTER_LABEL,
  PAGE_SIZE,
  VIEWS,
  VIEW_LABEL,
  keepListParams,
  parseListQuery,
  viewCount,
} from './model';

/**
 * Inquiry master/detail for one kind (events or catering). The URL is the
 * source of truth: `?view=&email=&q=&sort=&page=` for the list, the path for
 * the open record. Below 1280px the record replaces the list.
 */
export const InquiriesWorkspace: React.FC<{
  config: InquiryKindConfig;
  selectedId: number | null;
  listPath: string;
  recordPath: (id: number) => string;
  /** Extra control in the title row (catering: Inquiries / Links). */
  sectionSwitch?: React.ReactNode;
}> = ({ config, selectedId, listPath, recordPath, sectionSwitch }) => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const query = useMemo(() => parseListQuery(params), [params]);
  const [searchInput, setSearchInput] = useState(query.search);
  const search = useDebouncedValue(searchInput.trim(), 250);
  const searchId = `${config.kind}-inquiry-search`;
  const querySearch = useRef(query.search);
  querySearch.current = query.search;
  // Value the field and the URL last agreed on. Back/forward changes `q`
  // without touching the field; copying it in here keeps them matched.
  // Our own debounced write already equals this, so it is not copied back
  // over keystrokes still in the box.
  const agreedSearch = useRef(query.search);

  const setParam = useCallback(
    (patch: Record<string, string | null>) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) =>
            v ? next.set(k, v) : next.delete(k),
          );
          if (!('page' in patch)) next.delete('page');
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  useEffect(() => {
    if (query.search === agreedSearch.current) return;
    agreedSearch.current = query.search;
    setSearchInput(query.search);
  }, [query.search]);

  useEffect(() => {
    agreedSearch.current = search;
    if (search !== querySearch.current) setParam({ q: search || null });
  }, [search, setParam]);

  const list = useInquiryList(config, query);
  const summary = useInquirySummary(config);
  const inquiryActions = useInquiryActions(config);
  const [markingRead, setMarkingRead] = useState(false);
  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const counts = summary.data;

  const open = useCallback(
    (id: number) =>
      navigate(`${recordPath(id)}${keepListParams(params)}`, {
        replace: selectedId != null,
      }),
    [navigate, recordPath, params, selectedId],
  );
  const close = useCallback(
    () => navigate(`${listPath}${keepListParams(params)}`),
    [navigate, listPath, params],
  );

  const idx = items.findIndex((i) => i.id === selectedId);
  const prev = idx > 0 ? items[idx - 1] : null;
  const next = idx >= 0 && idx < items.length - 1 ? items[idx + 1] : null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      if (
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) ||
        t.isContentEditable
      )
        return;
      if (document.querySelector('[role="menu"], [aria-modal="true"]')) return;
      if (e.key === '/') {
        e.preventDefault();
        document.getElementById(searchId)?.focus();
        return;
      }
      if (e.key === 'Escape' && selectedId != null) {
        close();
        return;
      }
      const down = e.key === 'ArrowDown' || e.key === 'j';
      const up = e.key === 'ArrowUp' || e.key === 'k';
      if (!down && !up) return;
      if (selectedId == null) {
        if (!down) return;
        e.preventDefault();
        document
          .querySelector<HTMLElement>('[data-inquiry-list] [tabindex="0"]')
          ?.focus();
        return;
      }
      const target = down ? next : prev;
      if (target) {
        e.preventDefault();
        open(target.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, next, prev, open, close, searchId]);

  const onSort = (key: SortKey) => {
    const dir =
      query.sortKey === key
        ? query.sortDir === 'asc'
          ? 'desc'
          : 'asc'
        : key === 'guest' || key === 'occasion' || key === 'date'
          ? 'asc'
          : 'desc';
    setParam({
      sort:
        key === 'received' && dir === 'desc'
          ? null
          : `${dir === 'desc' ? '-' : ''}${key}`,
    });
  };

  const detailOpen = selectedId != null;
  const filtered = !!query.search || query.email !== 'any';

  return (
    <Workspace
      title={
        <span className="inline-flex items-center gap-2.5">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-v2 bg-v2-accent/10 text-v2-accent">
            {config.kind === 'event' ? (
              <IconCalendar size={16} />
            ) : (
              <IconReceipt size={16} />
            )}
          </span>
          {config.title}
        </span>
      }
      description={config.description}
      meta={
        counts ? (
          <Tag tone="accent">{counts.total.toLocaleString()} total</Tag>
        ) : undefined
      }
      actions={
        <>
          <Button
            size="sm"
            variant="ghost"
            disabled={markingRead || (counts?.unseen ?? 0) <= 0}
            onClick={() => {
              setMarkingRead(true);
              void inquiryActions
                .markAllRead()
                .catch(() => undefined)
                .finally(() => setMarkingRead(false));
            }}
          >
            Mark all as read
          </Button>
          <RefreshStatus
            updatedAt={list.dataUpdatedAt || null}
            fetching={list.isFetching && !list.isPending}
            className="hidden lg:inline-flex"
          />
          {sectionSwitch}
        </>
      }
      toolbar={
        <PageHeaderBand compact>
          <Segmented<View>
            aria-label="View"
            className="shrink-0"
            value={query.view}
            onChange={(v) => setParam({ view: v === 'inbox' ? null : v })}
            items={VIEWS.map((v) => ({
              key: v,
              label: VIEW_LABEL[v],
              count: viewCount(counts, v),
            }))}
          />
          <span
            aria-hidden
            className="mx-1 hidden h-5 w-px bg-v2-line md:block"
          />
          <Select
            size="sm"
            aria-label="Team email"
            value={query.email}
            onChange={(e) =>
              setParam({
                email: e.target.value === 'any' ? null : e.target.value,
              })
            }
            className="hidden w-[168px] shrink-0 md:block"
          >
            {EMAIL_FILTERS.map((f: EmailFilter) => (
              <option key={f} value={f}>
                {EMAIL_FILTER_LABEL[f]}
              </option>
            ))}
          </Select>
          <div className="ml-auto w-[260px] min-w-[160px] shrink">
            <Input
              id={searchId}
              size="sm"
              type="search"
              placeholder={config.searchPlaceholder}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              leading={<IconSearch size={14} />}
              trailing={!searchInput ? <Kbd>/</Kbd> : undefined}
              aria-label="Search inquiries"
            />
          </div>
        </PageHeaderBand>
      }
    >
      <div className="flex min-h-0 flex-1">
        <div
          data-inquiry-list
          className={clsx(
            'flex min-h-0 flex-col bg-v2-surface',
            detailOpen
              ? 'hidden w-[360px] shrink-0 border-r border-v2-line xl:flex'
              : 'min-w-0 flex-1',
          )}
        >
          {list.isRefetchError && list.data && (
            <RefreshFailed
              className="m-3"
              what={config.title}
              onRetry={() => void list.refetch()}
            />
          )}
          {list.isError && !list.data ? (
            <div className="p-4 lg:p-6">
              <Callout tone="danger" title="Couldn’t load inquiries">
                {list.error.message}
                <div className="mt-2">
                  <Button size="sm" onClick={() => void list.refetch()}>
                    Retry
                  </Button>
                </div>
              </Callout>
            </div>
          ) : !list.isPending && items.length === 0 ? (
            <EmptyList
              config={config}
              view={query.view}
              search={query.search}
              filtered={filtered}
              onClear={() => {
                setSearchInput('');
                setParam({ q: null, email: null });
              }}
            />
          ) : (
            <>
              <div
                className={clsx(
                  'v2-scroll min-h-0 flex-1 overflow-auto transition-opacity',
                  list.isFetching && list.isPlaceholderData && 'opacity-70',
                )}
              >
                {detailOpen ? (
                  <InquiryCompactList
                    config={config}
                    items={items}
                    selectedId={selectedId}
                    onSelect={open}
                    loading={list.isPending}
                  />
                ) : (
                  <InquiryTable
                    config={config}
                    items={items}
                    selectedId={selectedId}
                    onSelect={open}
                    loading={list.isPending}
                    sortKey={query.sortKey}
                    sortDir={query.sortDir}
                    onSort={onSort}
                  />
                )}
              </div>
              {list.data && list.data.totalPages > 1 ? (
                <Pagination
                  page={query.page}
                  totalPages={list.data.totalPages}
                  totalCount={list.data.totalCount}
                  pageSize={PAGE_SIZE}
                  onPage={(p) => setParam({ page: p > 1 ? String(p) : null })}
                  noun="inquiries"
                />
              ) : (
                list.data &&
                !detailOpen && (
                  <div className="flex h-10 shrink-0 items-center justify-between border-t border-v2-line px-4 text-v2-12 text-v2-fg-3 lg:px-6">
                    <span className="v2-num">
                      Showing {items.length.toLocaleString()} of{' '}
                      {list.data.totalCount.toLocaleString()}{' '}
                      {list.data.totalCount === 1 ? 'inquiry' : 'inquiries'}
                    </span>
                    <span className="hidden items-center gap-1.5 lg:inline-flex">
                      <Kbd>↑</Kbd>
                      <Kbd>↓</Kbd> move · <Kbd>↵</Kbd> open · <Kbd>/</Kbd>{' '}
                      search
                    </span>
                  </div>
                )
              )}
            </>
          )}
        </div>

        {detailOpen && (
          <div className="v2-slide-in-right flex min-h-0 min-w-0 flex-1 flex-col">
            <InquiryDetail
              key={selectedId}
              config={config}
              id={selectedId}
              row={items.find((i) => i.id === selectedId) ?? null}
              onClose={close}
              onPrev={prev ? () => open(prev.id) : undefined}
              onNext={next ? () => open(next.id) : undefined}
              showBack
            />
          </div>
        )}
      </div>
    </Workspace>
  );
};

const EmptyList: React.FC<{
  config: InquiryKindConfig;
  view: View;
  search: string;
  filtered: boolean;
  onClear: () => void;
}> = ({ config, view, search, filtered, onClear }) => {
  if (filtered) {
    return (
      <EmptyState
        icon={<IconSearch />}
        title={
          search
            ? `No inquiries match “${search}”`
            : 'No inquiries match this filter'
        }
        description="Try a different spelling, or clear the filters to see everything in this view."
        action={
          <Button size="sm" onClick={onClear}>
            Clear filters
          </Button>
        }
      />
    );
  }
  if (view === 'inbox' || view === 'new' || view === 'to_contact') {
    return (
      <EmptyState
        icon={<IconInbox />}
        title={
          view === 'new'
            ? 'No new inquiries'
            : view === 'to_contact'
              ? 'Nothing waiting to contact'
              : 'Nothing needs attention'
        }
        description="New inquiries from Arthur show up here with a summary and transcript."
      />
    );
  }
  const closedTitle: Partial<Record<View, string>> = {
    won: 'No won inquiries',
    lost: 'No lost inquiries',
    archived: 'Nothing archived',
  };
  return (
    <EmptyState
      icon={<IconSparkle />}
      title={closedTitle[view] ?? 'No inquiries yet'}
      description={view === 'all' ? config.emptyAll : undefined}
    />
  );
};
